/**
 * Campana de notificaciones (F3-C): contador de no-leídas (query local) +
 * dropdown con las recientes; marcar-leído al pulsar (write-path read_at) y
 * deep-link por kind. Local-first: todo resuelve offline.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Bell } from "lucide-solid";
import { markNotificationReadMutation } from "@space/shared";
import type { Notification } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { Link, useNavigate } from "@/lib/u-router";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMyNotifications, useUnreadNotifications } from "../../hooks/use-community-data";
import { notificationView } from "./notification-meta";

/** Máximo mostrado en el badge antes de "9+". */
const BADGE_CAP = 9;

export const NotificationsBell: Component<{
  /**
   * Slot del layout donde se monta la campana. El layout la monta TRES veces
   * (sidebar de escritorio + barra móvil + la copia de la sidebar que vive en
   * el cajón móvil, que está siempre montado para poder animar su cierre).
   * Todas coexisten en el DOM: las que no tocan solo están ocultas por CSS, y
   * `display:none` no saca un nodo del documento. Los testids deben ser ÚNICOS
   * por montaje (strict mode de Playwright), así que cada slot que no sea el de
   * escritorio los sufija.
   */
  slot?: "desktop" | "mobile" | "drawer";
}> = (props) => {
  const tid = (name: string) =>
    props.slot && props.slot !== "desktop" ? `${name}-${props.slot}` : name;
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const navigate = useNavigate();
  const unread = useUnreadNotifications();
  const recent = useMyNotifications();
  const [open, setOpen] = createSignal(false);

  const unreadCount = createMemo(() => (unread.data ?? []).length);

  const onPick = (n: Notification) => {
    const db = drizzle();
    if (db && !n.read_at) {
      void markNotificationReadMutation(db, n.id);
    }
    setOpen(false);
    void navigate({ to: notificationView(n).to });
  };

  return (
    // Anclado en el `slot` de origen, pero SIN posicionar a mano: el panel se
    // colocaba con `absolute right-0` + `w-80`, o sea pegaba su borde derecho al
    // del botón y crecía 320 px hacia la izquierda. En la barra móvil eso está
    // bien (el botón vive a la derecha), pero en ESCRITORIO la campana está en la
    // barra lateral IZQUIERDA, así que el panel se salía del viewport y quedaba
    // medio cortado. Kobalte reposiciona contra los bordes (flip/shift), que es
    // lo que un cálculo fijo no puede hacer: la respuesta correcta depende de
    // dónde esté el disparador y de cuánto mida la ventana en ese momento.
    <Popover
      open={open()}
      onOpenChange={setOpen}
      placement={props.slot === "mobile" ? "bottom-end" : "bottom-start"}
      gutter={8}
      flip
      // Que nunca bese el borde de la ventana aunque tenga que desplazarse.
      overflowPadding={12}
    >
      <PopoverTrigger
        data-testid={tid("notifications-bell")}
        aria-label={t(msg`Notifications`)}
        class="relative rounded-xl p-2 text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent"
      >
        <Bell class="h-5 w-5" />
        <Show when={unreadCount() > 0}>
          <span
            data-testid={tid("notifications-badge")}
            class="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-danger-foreground"
          >
            {unreadCount() > BADGE_CAP ? `${BADGE_CAP}+` : unreadCount()}
          </span>
        </Show>
      </PopoverTrigger>

      <PopoverContent
        data-testid={tid("notifications-dropdown")}
        // El ancho también se acota a la ventana: en pantallas estrechas un 20rem
        // fijo vuelve a desbordar por el otro lado, y entonces no hay colocación
        // que lo salve.
        class="max-h-96 w-[min(20rem,calc(100vw-1.5rem))] overflow-y-auto rounded-2xl border border-border bg-surface p-2 shadow-xl"
      >
        <div class="flex items-center justify-between px-2 py-1.5">
          <span class="text-sm font-bold">{t(msg`Notifications`)}</span>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            class="text-xs text-accent hover:underline"
          >
            {t(msg`See all`)}
          </Link>
        </div>
        <ul class="space-y-1">
          <For
            each={(recent.data ?? []).slice(0, 8)}
            fallback={
              <li class="px-2 py-3 text-center text-xs text-muted-foreground">
                {t(msg`No notifications yet.`)}
              </li>
            }
          >
            {(n) => (
              <li>
                <button
                  type="button"
                  data-testid={tid("notification-item")}
                  data-unread={n.read_at ? "false" : "true"}
                  onClick={() => onPick(n)}
                  class="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left text-sm hover:bg-accent/10"
                  classList={{ "font-semibold": !n.read_at }}
                >
                  <Show when={!n.read_at}>
                    <span class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />
                  </Show>
                  <span class="flex-1">{t(notificationView(n).title)}</span>
                </button>
              </li>
            )}
          </For>
        </ul>
      </PopoverContent>
    </Popover>
  );
};
